import React, { FC, ReactNode, useEffect, useRef, useState } from "react";
import { Box, Button, Chip, CircularProgress, TextField, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import axiosInstant from "../../../api";
import { matchIntakeProducts } from "../../../api/endpoints";
import InlineBrandSelector from "../../videoProduction/components/InlineBrandSelector";
import { InlineProductSelector } from "../../videoProduction/components/InlineProductSelector";

// Brief intake match step: before a brief-drafted order reaches Review, the
// user decides for the brand and for every product whether it is one that
// already exists or a new one. Nothing is pre-chosen as "new" — only exact
// name matches are pre-selected — so a duplicate needs a deliberate click.

export interface IntakeBrandOption {
    id: number;
    name: string;
    slug?: string;
    logo?: string;
    rmn?: boolean;
    deactivated?: boolean;
    // Supplier candidates: networks (retailer/agency roots) it is linked to.
    network_ids?: number[];
}

export interface IntakeProductCandidate {
    desireid: number;
    title: string;
    image?: string | null;
    url?: string;
}

export interface IntakeProductRow {
    name: string;
    short_name: string;
    pdp_url: string;
    // Same-name product on the suggested brand, or null.
    desireid: number | null;
    candidates: IntakeProductCandidate[];
}

export interface IntakeBrandReview {
    supplier_name: string;
    match_id: number | null;
    candidates: IntakeBrandOption[];
}

// The retailer or agency the brief names (owner of the order).
export interface IntakeNetworkReview {
    name: string;
    match_id: number | null;
    candidates: IntakeBrandOption[];
}

// "none" = the supplier brand owns the order directly.
export type IntakeNetworkDecision = { kind: "existing"; brand: IntakeBrandOption } | { kind: "none" };

export type IntakeBrandDecision =
    | { kind: "existing"; brand: IntakeBrandOption }
    | { kind: "new"; name: string };

export type IntakeProductDecision =
    | { kind: "existing"; product: IntakeProductCandidate }
    | { kind: "new"; name: string };

export interface IntakeDecisions {
    network: IntakeNetworkDecision | null;
    brand: IntakeBrandDecision | null;
    products: (IntakeProductDecision | null)[];
    // Product rows as last matched, and the brand they were matched against
    // (null = none). A different chosen brand triggers a re-match.
    rows: IntakeProductRow[];
    rowsBrandId: number | null;
}

// Exact name matches pre-selected; everything else left for the user.
export const initialIntakeDecisions = (
    networkReview: IntakeNetworkReview,
    review: IntakeBrandReview,
    rows: IntakeProductRow[]
): IntakeDecisions => {
    const matched = review.candidates.find((b) => b.id === review.match_id) || null;
    const network = networkReview.candidates.find((b) => b.id === networkReview.match_id) || null;
    let networkDecision: IntakeNetworkDecision | null = null;
    if (network) networkDecision = { kind: "existing", brand: network };
    else if (!networkReview.name) networkDecision = { kind: "none" };
    return {
        network: networkDecision,
        brand: matched ? { kind: "existing", brand: matched } : null,
        products: rows.map((row) => preselect(row)),
        rows,
        rowsBrandId: review.match_id,
    };
};

const preselect = (row: IntakeProductRow): IntakeProductDecision | null => {
    const exact = row.candidates.find((c) => c.desireid === row.desireid);
    return exact ? { kind: "existing", product: exact } : null;
};

const isDecided = (decision: IntakeBrandDecision | IntakeProductDecision | null) =>
    !!decision && (decision.kind === "existing" || decision.name.trim().length > 0);

const labelSx = {
    fontSize: 10.5,
    fontWeight: 700,
    color: "var(--accent-text)",
    letterSpacing: 0.6,
    textTransform: "uppercase" as const,
    fontFamily: "CircularStd",
};

const tagSx = {
    fontSize: 9.5,
    fontWeight: 700,
    height: 18,
    borderRadius: "99px",
    fontFamily: "CircularStd",
};

const OptionRow: FC<{
    selected: boolean;
    onSelect: () => void;
    label: ReactNode;
    tags?: ReactNode;
    children?: ReactNode;
}> = ({ selected, onSelect, label, tags, children }) => (
    <Box
        sx={{
            border: selected ? "1.5px solid var(--accent)" : "1px solid var(--border)",
            backgroundColor: selected ? "var(--tint)" : "var(--surface)",
            borderRadius: "10px",
            transition: "border-color .15s, background-color .15s",
        }}
    >
        <Box
            component="button"
            type="button"
            onClick={onSelect}
            sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                px: "12px",
                py: "9px",
                background: "none",
                border: "none",
                cursor: "pointer",
                textAlign: "left",
            }}
        >
            <Box
                sx={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    flexShrink: 0,
                    border: selected ? "5px solid var(--accent)" : "1.5px solid var(--muted)",
                    backgroundColor: "var(--surface)",
                }}
            />
            <Typography
                component="span"
                sx={{ fontSize: 13, color: "var(--text)", fontFamily: "CircularStd", flex: 1, minWidth: 0 }}
            >
                {label}
            </Typography>
            {tags}
        </Box>
        {selected && children && <Box sx={{ px: "12px", pb: "10px", pl: "38px" }}>{children}</Box>}
    </Box>
);

interface IntakeMatchStepProps {
    networkReview: IntakeNetworkReview;
    review: IntakeBrandReview;
    decisions: IntakeDecisions;
    onBack: () => void;
    onConfirm: (decisions: IntakeDecisions) => void;
}

export const IntakeMatchStep: FC<IntakeMatchStepProps> = ({ networkReview, review, decisions, onBack, onConfirm }) => {
    const [network, setNetwork] = useState<IntakeNetworkDecision | null>(decisions.network);
    const [networkSearching, setNetworkSearching] = useState(
        decisions.network?.kind === "existing" &&
            !networkReview.candidates.some(
                (b) => b.id === (decisions.network as { brand: IntakeBrandOption }).brand.id
            )
    );
    const [brand, setBrand] = useState<IntakeBrandDecision | null>(decisions.brand);
    const [products, setProducts] = useState<(IntakeProductDecision | null)[]>(decisions.products);
    const [rows, setRows] = useState<IntakeProductRow[]>(decisions.rows);
    const [newBrandName, setNewBrandName] = useState(
        decisions.brand?.kind === "new" ? decisions.brand.name : review.supplier_name || ""
    );
    // "A different existing brand" / "Search the catalog" rows are open.
    const [brandSearching, setBrandSearching] = useState(
        decisions.brand?.kind === "existing" &&
            !review.candidates.some((b) => b.id === (decisions.brand as { brand: IntakeBrandOption }).brand.id)
    );
    const [productSearching, setProductSearching] = useState<number[]>(() =>
        decisions.products
            .map((d, i) =>
                d?.kind === "existing" &&
                !decisions.rows[i]?.candidates.some((c) => c.desireid === d.product.desireid)
                    ? i
                    : -1
            )
            .filter((i) => i >= 0)
    );
    const [loadingProducts, setLoadingProducts] = useState(false);
    // Brand the current `rows` candidates were matched against.
    const matchedForRef = useRef<number | null>(decisions.rowsBrandId);

    const brandId = brand?.kind === "existing" ? brand.brand.id : null;

    // Re-match the brief's products whenever the chosen existing brand changes.
    useEffect(() => {
        let cancelled = false;
        const cancel = () => {
            cancelled = true;
        };
        if (brandId == null || brandId === matchedForRef.current) return cancel;
        setLoadingProducts(true);
        axiosInstant
            .post(matchIntakeProducts(), { brand_id: brandId, names: rows.map((r) => r.name) })
            .then(({ data }) => {
                if (cancelled) return;
                const next = rows.map((row, i) => ({
                    ...row,
                    desireid: data.products[i]?.desireid ?? null,
                    candidates: data.products[i]?.candidates || [],
                }));
                matchedForRef.current = brandId;
                setRows(next);
                setProducts(next.map((row) => preselect(row)));
                setProductSearching([]);
            })
            .catch(() => {
                if (cancelled) return;
                const next = rows.map((row) => ({ ...row, desireid: null, candidates: [] }));
                matchedForRef.current = brandId;
                setRows(next);
                setProducts(next.map(() => null));
                setProductSearching([]);
            })
            .finally(() => {
                if (!cancelled) setLoadingProducts(false);
            });
        return cancel;
    }, [brandId]);

    // A new brand has no catalog: every product on it is new.
    useEffect(() => {
        if (brand?.kind !== "new") return;
        matchedForRef.current = null;
        setProductSearching([]);
        setRows((prev) => prev.map((row) => ({ ...row, desireid: null, candidates: [] })));
        setProducts((prev) =>
            prev.map((decision, i) =>
                decision?.kind === "new" ? decision : { kind: "new", name: rows[i]?.name || "" }
            )
        );
    }, [brand?.kind]);

    const setProduct = (index: number, decision: IntakeProductDecision | null, searching = false) => {
        setProducts((prev) => prev.map((d, i) => (i === index ? decision : d)));
        setProductSearching((prev) => {
            const without = prev.filter((i) => i !== index);
            return searching ? [...without, index] : without;
        });
    };

    const undecidedProducts = products.filter((d) => !isDecided(d)).length;
    const canContinue = !!network && isDecided(brand) && undecidedProducts === 0 && !loadingProducts;
    const networkBrand = network?.kind === "existing" ? network.brand : null;
    const linkedToNetwork = (candidate: IntakeBrandOption) =>
        !!networkBrand && (candidate.network_ids || []).includes(networkBrand.id);
    const newCount =
        (brand?.kind === "new" ? 1 : 0) + products.filter((d) => d?.kind === "new").length;

    let brandName = "";
    if (brand?.kind === "existing") brandName = brand.brand.name;
    else if (brand?.kind === "new") brandName = brand.name;

    const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;
    let footerStatus = "Everything matches existing records";
    if (!network) footerStatus = "Choose the retailer or agency";
    else if (!isDecided(brand)) footerStatus = "Choose the supplier brand";
    else if (undecidedProducts > 0) footerStatus = `${plural(undecidedProducts, "product")} still to choose`;
    else if (newCount > 0) footerStatus = `${plural(newCount, "new record")} will be created on submit`;

    return (
        <>
            <Box sx={{ flex: 1, overflowY: "auto", p: { xs: "20px 16px 22px", sm: "22px 36px 24px" } }}>
                <Typography
                    sx={{
                        fontSize: 22,
                        fontWeight: 700,
                        color: "var(--text)",
                        letterSpacing: -0.4,
                        fontFamily: "CircularStd",
                        mb: "6px",
                    }}
                >
                    Confirm brand & products
                </Typography>
                <Typography sx={{ fontSize: 13.5, color: "var(--text-2)", mb: "20px", fontFamily: "CircularStd" }}>
                    Match what the brief names to what&apos;s already in the portal. Anything you mark as new is created
                    only when you submit the order.
                </Typography>

                {/* Retailer or agency: owns the order */}
                <Box mb="24px">
                    <Typography sx={{ ...labelSx, mb: "4px" }}>Retailer or agency · order brand</Typography>
                    <Typography sx={{ fontSize: 12.5, color: "var(--text-2)", fontFamily: "CircularStd", mb: "10px" }}>
                        {networkReview.name ? (
                            <>
                                The brief is from <strong>{networkReview.name}</strong>
                                {networkReview.candidates.length > 0 ? ". Possible matches:" : ". No matching retailer or agency was found."}
                            </>
                        ) : (
                            "The brief names no retailer or agency."
                        )}
                    </Typography>
                    <Box display="flex" flexDirection="column" gap="6px">
                        {networkReview.candidates.map((candidate) => (
                            <OptionRow
                                key={candidate.id}
                                selected={network?.kind === "existing" && network.brand.id === candidate.id && !networkSearching}
                                onSelect={() => {
                                    setNetworkSearching(false);
                                    setNetwork({ kind: "existing", brand: candidate });
                                }}
                                label={candidate.name}
                                tags={
                                    candidate.id === networkReview.match_id ? (
                                        <Chip label="MATCH" size="small" sx={{ ...tagSx, color: "var(--accent-text)", background: "var(--tint)" }} />
                                    ) : undefined
                                }
                            />
                        ))}
                        <OptionRow
                            selected={networkSearching}
                            onSelect={() => {
                                if (networkSearching) return;
                                setNetworkSearching(true);
                                setNetwork(null);
                            }}
                            label="A different retailer or agency"
                        >
                            <InlineBrandSelector
                                setFieldValue={() => undefined}
                                isRMN
                                showRmnToggle={false}
                                selectedBrand={network?.kind === "existing" ? network.brand : null}
                                setSelectedBrand={(picked: any) => {
                                    setNetwork(
                                        picked && picked.id
                                            ? {
                                                  kind: "existing",
                                                  brand: { id: Number(picked.id), name: picked.name, logo: picked.logo, rmn: true },
                                              }
                                            : null
                                    );
                                }}
                                disabled={false}
                                filterRmn
                                allowAdd={false}
                            />
                        </OptionRow>
                        <OptionRow
                            selected={network?.kind === "none"}
                            onSelect={() => {
                                setNetworkSearching(false);
                                setNetwork({ kind: "none" });
                            }}
                            label="None. The supplier brand owns this order"
                        />
                    </Box>
                </Box>

                {/* Supplier brand: owns the products and projects */}
                <Box mb="24px">
                    <Typography sx={{ ...labelSx, mb: "4px" }}>Supplier brand</Typography>
                    <Typography sx={{ fontSize: 12.5, color: "var(--text-2)", fontFamily: "CircularStd", mb: "10px" }}>
                        The brief names <strong>{review.supplier_name || "no supplier"}</strong>
                        {review.candidates.length > 0 ? ". Possible matches:" : ". No similar brand was found."}
                    </Typography>
                    <Box display="flex" flexDirection="column" gap="6px">
                        {review.candidates.map((candidate) => (
                            <OptionRow
                                key={candidate.id}
                                selected={brand?.kind === "existing" && brand.brand.id === candidate.id && !brandSearching}
                                onSelect={() => {
                                    setBrandSearching(false);
                                    setBrand({ kind: "existing", brand: candidate });
                                }}
                                label={candidate.name}
                                tags={
                                    <>
                                        {candidate.id === review.match_id && (
                                            <Chip label="SAME NAME" size="small" sx={{ ...tagSx, color: "var(--accent-text)", background: "var(--tint)" }} />
                                        )}
                                        {linkedToNetwork(candidate) && (
                                            <Chip
                                                label={`ON ${networkBrand ? networkBrand.name.toUpperCase() : ""}`}
                                                size="small"
                                                sx={{ ...tagSx, color: "var(--text-2)" }}
                                            />
                                        )}
                                        {candidate.deactivated && (
                                            <Chip label="DEACTIVATED" size="small" sx={{ ...tagSx, color: "var(--text-2)" }} />
                                        )}
                                    </>
                                }
                            />
                        ))}
                        <OptionRow
                            selected={brandSearching}
                            onSelect={() => {
                                if (brandSearching) return;
                                setBrandSearching(true);
                                setBrand(null);
                            }}
                            label="A different existing brand"
                        >
                            <InlineBrandSelector
                                setFieldValue={() => undefined}
                                isRMN={false}
                                showRmnToggle={false}
                                selectedBrand={brand?.kind === "existing" ? brand.brand : null}
                                setSelectedBrand={(picked: any) => {
                                    setBrand(
                                        picked && picked.id
                                            ? {
                                                  kind: "existing",
                                                  brand: {
                                                      id: Number(picked.id),
                                                      name: picked.name,
                                                      slug: picked.slug,
                                                      logo: picked.logo,
                                                      rmn: !!picked.rmn,
                                                  },
                                              }
                                            : null
                                    );
                                }}
                                disabled={false}
                                filterRmn={undefined}
                                allowAdd={false}
                            />
                        </OptionRow>
                        <OptionRow
                            selected={brand?.kind === "new"}
                            onSelect={() => {
                                setBrandSearching(false);
                                setBrand({ kind: "new", name: newBrandName });
                            }}
                            label="Create a new brand"
                        >
                            <TextField
                                size="small"
                                fullWidth
                                value={newBrandName}
                                placeholder="Brand name"
                                onChange={(e) => {
                                    setNewBrandName(e.target.value);
                                    setBrand({ kind: "new", name: e.target.value });
                                }}
                            />
                            {review.candidates.length > 0 && (
                                <Typography sx={{ fontSize: 11.5, color: "var(--accent-text)", fontFamily: "CircularStd", mt: "6px" }}>
                                    Similar brands exist above. Make sure this isn&apos;t one of them.
                                </Typography>
                            )}
                        </OptionRow>
                    </Box>
                    {networkBrand && brand && !(brand.kind === "existing" && linkedToNetwork(brand.brand)) && (
                        <Typography sx={{ fontSize: 11.5, color: "var(--text-2)", fontFamily: "CircularStd", mt: "8px" }}>
                            {brandName || "The supplier"} will be added to {networkBrand.name}&apos;s brands when you submit
                            {brand.kind === "existing" ? ", if it isn't there already" : ""}.
                        </Typography>
                    )}
                </Box>

                {/* Products */}
                <Box>
                    <Box display="flex" alignItems="center" gap="8px" mb="4px">
                        <Typography sx={labelSx}>Products · {rows.length}</Typography>
                        {loadingProducts && <CircularProgress size={12} />}
                    </Box>
                    {!brand ? (
                        <Typography sx={{ fontSize: 12.5, color: "var(--text-2)", fontFamily: "CircularStd" }}>
                            Choose the brand first. Its catalog is searched for each product.
                        </Typography>
                    ) : (
                        <Box display="flex" flexDirection="column" gap="16px" mt="8px">
                            {rows.map((row, index) => {
                                const decision = products[index];
                                const searching = productSearching.includes(index);
                                return (
                                    <Box key={`${row.name}-${index}`}>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "var(--text)", fontFamily: "CircularStd" }}>
                                            {row.name}
                                        </Typography>
                                        {row.pdp_url && (
                                            <Typography
                                                className="truncate"
                                                sx={{ fontSize: 11.5, color: "var(--muted)", fontFamily: "CircularStd" }}
                                            >
                                                {row.pdp_url}
                                            </Typography>
                                        )}
                                        <Box display="flex" flexDirection="column" gap="6px" mt="6px">
                                            {brand.kind === "existing" &&
                                                row.candidates.map((candidate) => (
                                                    <OptionRow
                                                        key={candidate.desireid}
                                                        selected={
                                                            !searching &&
                                                            decision?.kind === "existing" &&
                                                            decision.product.desireid === candidate.desireid
                                                        }
                                                        onSelect={() =>
                                                            setProduct(index, { kind: "existing", product: candidate })
                                                        }
                                                        label={candidate.title}
                                                        tags={
                                                            candidate.desireid === row.desireid ? (
                                                                <Chip label="SAME NAME" size="small" sx={{ ...tagSx, color: "var(--accent-text)", background: "var(--tint)" }} />
                                                            ) : undefined
                                                        }
                                                    />
                                                ))}
                                            {brand.kind === "existing" && (
                                                <OptionRow
                                                    selected={searching}
                                                    onSelect={() => {
                                                        if (!searching) setProduct(index, null, true);
                                                    }}
                                                    label={`Another product from ${brandName}`}
                                                >
                                                    <InlineProductSelector
                                                        setFieldValue={() => undefined}
                                                        selectedProduct={
                                                            decision?.kind === "existing"
                                                                ? { desireid: decision.product.desireid, id: decision.product.desireid, title: decision.product.title }
                                                                : null
                                                        }
                                                        setSelectedProduct={(picked: any) => {
                                                            const id = picked ? Number(picked.desireid ?? picked.id) : NaN;
                                                            setProduct(
                                                                index,
                                                                Number.isFinite(id)
                                                                    ? {
                                                                          kind: "existing",
                                                                          product: { desireid: id, title: picked.title, image: picked.image },
                                                                      }
                                                                    : null,
                                                                true
                                                            );
                                                        }}
                                                        selectedBrandId={brand.brand.id}
                                                        selectedBrandName={brand.brand.name}
                                                        allowAdd={false}
                                                    />
                                                </OptionRow>
                                            )}
                                            <OptionRow
                                                selected={decision?.kind === "new"}
                                                onSelect={() =>
                                                    setProduct(index, {
                                                        kind: "new",
                                                        name: decision?.kind === "new" ? decision.name : row.name,
                                                    })
                                                }
                                                label="Create a new product"
                                            >
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    value={decision?.kind === "new" ? decision.name : ""}
                                                    placeholder="Product name"
                                                    onChange={(e) => setProduct(index, { kind: "new", name: e.target.value })}
                                                />
                                                {brand.kind === "existing" && row.candidates.length > 0 && (
                                                    <Typography sx={{ fontSize: 11.5, color: "var(--accent-text)", fontFamily: "CircularStd", mt: "6px" }}>
                                                        Similar products exist above. Make sure this isn&apos;t one of them.
                                                    </Typography>
                                                )}
                                            </OptionRow>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "12px",
                    p: { xs: "12px 18px", sm: "14px 28px" },
                    borderTop: "1px solid var(--border)",
                    background: "var(--surface)",
                    flexShrink: 0,
                }}
            >
                <Button
                    onClick={onBack}
                    startIcon={<ArrowBackIcon />}
                    sx={{ textTransform: "capitalize", fontFamily: "CircularStd", color: "var(--text)" }}
                >
                    Back
                </Button>
                <Typography sx={{ fontSize: 12, color: "var(--text-2)", fontFamily: "CircularStd", textAlign: "right", flex: 1 }}>
                    {footerStatus}
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={!canContinue}
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => brand && onConfirm({ network, brand, products, rows, rowsBrandId: matchedForRef.current })}
                    sx={{ textTransform: "capitalize", fontFamily: "CircularStd", borderRadius: "24px", px: 3 }}
                >
                    Confirm
                </Button>
            </Box>
        </>
    );
};
